/**
 * Store-dispatching half of plan-lock handling — routes a detected
 * plan/quota-restriction error to the global PlanLockDialog. Kept separate
 * from plan-lock-detection.util.ts (which has no store dependency) so
 * api.service.ts can detect these errors without importing the store —
 * see that file's header comment for why.
 *
 * Import this from React components/hooks and from call-site catch blocks
 * (not from api.service.ts).
 */

import { store } from '@/store';
import { hidePlanLock, showPlanLock } from '@/store/slices/planLockSlice';
import { ApiErrorBody, lockPayloadFor } from './plan-lock-detection.util';
import { refreshWalletSummary } from '@/hooks/useWalletSummary';

/**
 * Pure check, no dispatch — was this error tagged as a plan/quota lock?
 * Mirrors permission-lock.util.ts's `isPermissionLockTagged`. Use this
 * (rather than `showPlanLockIfTagged`) when a plan lock is an EXPECTED,
 * non-actionable outcome for the request in question — a background or
 * supplementary fetch for a module the tenant's plan simply doesn't include
 * — so the call site can skip its own inline error without also popping a
 * modal the user didn't ask for.
 */
export function isPlanLockTagged(error: any): boolean {
  return !!error?.planLock;
}

/**
 * Call from an axios-based catch block, on an error that already passed
 * through the interceptor. Returns true if the lock dialog was shown
 * (caller should skip its own setError/Alert in that case).
 */
export function showPlanLockIfTagged(error: any): boolean {
  if (error?.planLock) {
    // A wallet refusal means the balance moved — refresh the banner/sidebar now.
    if (error.planLock.kind === 'wallet') void refreshWalletSummary(true);
    store.dispatch(showPlanLock(error.planLock));
    return true;
  }
  return false;
}

/**
 * Call from a raw-fetch catch block (no axios interceptor tags these) with
 * the parsed response body's `error` field. Returns true if the lock dialog
 * was shown.
 */
export function showPlanLockFromErrorBody(apiError?: ApiErrorBody): boolean {
  const payload = lockPayloadFor(apiError);
  if (!payload) return false;
  store.dispatch(showPlanLock(payload));
  return true;
}

export function closePlanLock(): void {
  store.dispatch(hidePlanLock());
}
